/**
 * InferenceModelsSection component.
 *
 * This component renders the inference models section of the InferenceModelsForSlides
 * component. It displays organized inference models grouped by stain type and handles
 * the selection and configuration of model orchestrations.
 */

import { Divider, Grid, Typography } from '@mui/material';
import { keyBy, map, size } from 'lodash';
import React from 'react';

import { modelTypeCellSegmentation } from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  ClassificationBinningParams,
  OrchestrationInference,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { ModelPreferencesById, ModelsByStainByType, ModelUserPreferences } from 'utils/models';
import { OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { BinaryClassifierSelectionMode } from '../../../binaryClassifierSelections';
import { inputCategoryLabels } from '../../../calculateFeaturesInputs';
import InferenceStain from './InferenceStain';

export interface InferenceModelsSectionProps {
  studyId: string;
  modelsByStain: ModelsByStainByType;
  slides: SlideInferenceResults[];
  selectedOrchestrations?: OrchestrationBySlideByType;
  setSelectedOrchestrationsByTypeByStain: (
    slideIds: string[],
    model?: any,
    modelType?: string,
    orchestration?: any
  ) => void;
  modelPreferencesByModelId?: ModelPreferencesById;
  setModelPreferencesByModelId?: (modelId: string, userPreferences: ModelUserPreferences) => void;
  setSelectedOrchestrationsByClassificationType: (modelType: string, previousModelType?: string) => void;
  setIntensityClassificationBinning?: (modelId: string, binning: ClassificationBinningParams) => void;
  binaryClassifierSelectionMode?: BinaryClassifierSelectionMode;
  setBinaryClassifierSelectionMode?: (mode: BinaryClassifierSelectionMode) => void;
  setSelectedOrchestrationsByResultName?: (
    slideIds: string[],
    resultName: string,
    orchestration: OrchestrationInference
  ) => void;
  disableIfNoJob?: boolean;
}

/**
 * Component for rendering the inference models section.
 *
 * This component displays inference models organized by stain type and provides
 * controls for selecting and configuring model orchestrations. It handles the
 * presentation layer for model selection and preferences.
 */
const InferenceModelsSection: React.FC<InferenceModelsSectionProps> = ({
  studyId,
  modelsByStain,
  slides,
  selectedOrchestrations,
  setSelectedOrchestrationsByTypeByStain,
  setSelectedOrchestrationsByResultName,
  modelPreferencesByModelId,
  setModelPreferencesByModelId,
  setSelectedOrchestrationsByClassificationType,
  setIntensityClassificationBinning,
  binaryClassifierSelectionMode,
  setBinaryClassifierSelectionMode,
  disableIfNoJob = false,
}) => {
  const slidesById = keyBy(slides, 'slideId');

  return (
    <Grid item container spacing={1}>
      <Grid item>
        <Typography variant="subtitle1" fontWeight={700}>
          {inputCategoryLabels.inference_results}:
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1} direction="column">
          {map(modelsByStain, (stainModelInferences, stain) => {
            return (
              <Grid item key={stain}>
                <InferenceStain
                  studyId={studyId}
                  key={stain}
                  stain={stain}
                  stainModelInferences={stainModelInferences}
                  slides={slidesById}
                  selectedOrchestrations={selectedOrchestrations}
                  setSelectedOrchestrationsByTypeByStain={setSelectedOrchestrationsByTypeByStain}
                  setSelectedOrchestrationsByResultName={setSelectedOrchestrationsByResultName}
                  modelPreferencesByModelId={modelPreferencesByModelId}
                  setModelPreferencesByModelId={setModelPreferencesByModelId}
                  setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                  setIntensityClassificationBinning={setIntensityClassificationBinning}
                  binaryClassifierSelectionMode={binaryClassifierSelectionMode}
                  setBinaryClassifierSelectionMode={setBinaryClassifierSelectionMode}
                  defaultExpanded={
                    size(modelsByStain) === 1 ||
                    Boolean(stainModelInferences?.[modelTypeCellSegmentation.apiModelValue])
                  }
                  disableIfNoJob={disableIfNoJob}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
};

export default InferenceModelsSection;
